import { FC, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import FooterText from "../../components/footerText";
import HubInputField from "../../components/inputType";
import { AuthContext } from "../../context/authContext";
import { ToastContext } from "../../context/toastContext";
import { LoginSchema } from "../../validation/login";
import useFetch from "../../hooks/useFetch";
import { APIEndpoints } from "../../types/apiTypes";
import { LoginFormType, LoginAPIResponseType } from "./types";
import { ToastTypes } from "../../types";

import ContactAndSafetyInfo from "src/components/contactAndSafetyInfo";
import { TextAlignProps } from "src/components/contactAndSafetyInfo/types";
import { LogoBanner } from "src/assets";
import { reRouteRelevantUser } from "src/helpers/user";



const HubLogin: FC = (): JSX.Element => {
  const [isPasswordVisible, setPasswordVisibility] = useState<boolean>(false);

  const { control, handleSubmit } = useForm({
    mode: "all",
    resolver: yupResolver(LoginSchema),
  });
  const {
    userData: sessionSavedUser,
    createSession,
    restoreSession,
  } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const navigate = useNavigate();

  const {
    data: userData,
    loading,
    mutate,
    error,
    removeErrorAndData,
  } = useFetch<LoginAPIResponseType>(APIEndpoints.USER_SIGNIN);

  useEffect(() => {
      console.log("userData", userData);
    if(userData && userData.code === 403 && userData.message === "User is Blacklisted") {
       const redirect_url = process.env.REACT_APP_PUBLIC_REDIRECT_URL
        if (redirect_url) window.location.replace(redirect_url)
         //showToast([userData.status.message], ToastTypes.ERROR);
    }else if (userData && userData?.status?.code === 200) {
      
      const { status, token } = userData;
      createSession(
        {
          ...status.data.user,
          practice_created_date: status.data.address
            ? status.data.address.date
            : "",
          practice_name: status.data.address ? status.data.address.name : "",
          address: status.data.address ? status.data.address.ref_address : "",
        },
        status.data.sub_header,
        token
      );
      showToast([status.message], ToastTypes.INFO);

      const relevantRoute = reRouteRelevantUser(status.data.user, false);
      navigate(relevantRoute)
    } 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const submitForm = async (values: LoginFormType) => {
    if ((error && error.length > 0) || userData) removeErrorAndData();
    const isSessionExists = restoreSession();

    if (!isSessionExists) {
      mutate({ user: values }, {}, {
        ...(window.location.href.includes("demo") ? { demo: true } : {})
      });
    } else {
      showToast([`There is already an active user session`], ToastTypes.INFO);
      if (sessionSavedUser) {
        const relevantRoute = reRouteRelevantUser(sessionSavedUser, false);
        navigate(relevantRoute)
      }
    }
  };

  return (
    <div className="container-fluid login-sec d-flex flex-fill flex-column justify-content-between overflow-auto">
      <div className="container">
        <div className="row my-4 mb-0 mb-md-4">
          <div className="col-12 col-md-7">
            <img
              height="110px"
              className="img-fluid"
              src={LogoBanner}
              alt="Banner Info"
            />
          </div>
          <div className="col-12 col-md-5 pt-2">
            <ContactAndSafetyInfo
              alignText={TextAlignProps.TEXT_END}
              textColor="black"
            />
          </div>
        </div>
        <div className="login-hero">
          <div className="row mb-3">
            <div className="col-12 col-md-7"></div>
            <div className="col-12 col-md-5">
              <form className="card" onSubmit={handleSubmit(submitForm)}>
                <div className="card-header card-header-guest">
                  <h1 className="mb-0">Welcome to</h1>
                  <h3 className="fw-bold">MyBV360</h3>
                </div>

                <div className="card-body">
                  <Controller
                    name="email"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <label
                          className={"form-label justify-content-start mb-2"}
                        >
                          Email *
                        </label>
                        <HubInputField
                          inputId="email"
                          inputPlaceholder="Email"
                          isRowBased={false}
                          isRequired={true}
                          field={field}
                          error={error}
                          // inputLabel="Email"
                          inputType="email"
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="password"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <label
                          htmlFor="password"
                          className="form-label justify-content-start mb-2"
                        >
                          Password *
                        </label>
                        <div className="input-group mb-3">
                          <input
                            type={isPasswordVisible ? "text" : "password"}
                            placeholder="Password"
                            className="form-control"
                            {...field}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-dark"
                            onClick={() =>
                              setPasswordVisibility(
                                (isPasswordVisible) => !isPasswordVisible
                              )
                            }
                          >
                            {isPasswordVisible ? (
                              <i className="bi bi-eye-fill"></i>
                            ) : (
                              <i className="bi bi-eye-slash"></i>
                            )}
                          </button>
                        </div>
                        {error && (
                          <p className="mb-0 text-danger">{error.message}</p>
                        )}
                      </>
                    )}
                  />
                  <div className="">
                    <button
                      disabled={loading}
                      className="btn btn-primary"
                      type="submit"
                    >
                      {loading ? <i className="spinner-border me-2"></i> : null}
                      {window.location.pathname.includes("demo")
                        ? "Demo Log In"
                        : "Log In"}
                    </button>
                    <br />
                    <Link className={"mt-2"} to="/users/forgotpassword">
                      Forgot your password?
                    </Link>
                    <br />
                    <Link
                      to={
                        window.location.pathname.includes("demo")
                          ? "/users/sign_in"
                          : "/users/demo_sign_in"
                      }
                    >
                      {window.location.pathname.includes("demo")
                        ? "Normal Sign In"
                        : "Demo Sign In"}
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <FooterText />
      </div>
    </div>
  );
};

export default HubLogin;
